import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChange,
} from '@angular/core'
import {
  addDays,
  addMonths,
  format,
  isAfter,
  isBefore,
  isToday,
  isValid,
  startOfDay,
  isWithinInterval,
} from 'date-fns';
import { merge } from 'lodash';
import { DatesService } from '../../dates.service';

@Component({
  selector: 'calendar-month',
  styleUrls: ['./calendar-month.component.less'],
  templateUrl: './calendar-month.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarMonthComponent implements OnChanges {
  @Input() date: Date
  @Input() start: Date
  @Input() end: Date
  @Input() min: Date
  @Input() max: Date
  @Input() active: Date
  @Input() readonly: boolean
  @Input() side: 'start' | 'end'
  @Input() firstDayOfWeek: string;
  @Output() selectedDate: EventEmitter<{ start: Date, end: Date, active: Date }> = new EventEmitter()
  @Output() hoveredDate: EventEmitter<{ start: Date, end: Date}> = new EventEmitter()
  month: string
  year: string
  days: any[][]
  weekdayNames: string[]

  constructor(private changeRef: ChangeDetectorRef, private datesService: DatesService) { }

  ngOnChanges(changes: {[key: string]: SimpleChange}) {
    if (isValid(changes['date']?.currentValue)) {
      this.updateDays()
    }
    if (this.days && (isValid(changes['start']?.currentValue) || isValid(changes['end']?.currentValue))) {
      for (const week of this.days) {
        for (const day of week) {
          const currentDay = day['date']
          const props = {
            start: startOfDay(currentDay).getTime() === (this.start && startOfDay(this.start).getTime()),
            end: startOfDay(currentDay).getTime() === (this.end && startOfDay(this.end).getTime()),
            range: this.isDayInRange(currentDay),
            expand: (this.side == 'start') ? isBefore(currentDay, this.end) : isAfter(currentDay, this.start),
          }
          merge(day, props)
        }
      }
      this.changeRef.markForCheck()
    }
  }

  updateDate(selected: { date: Date, available: boolean }) {
    if (!this.readonly && selected.available) {
      if (!this.active) {
        if (this.side == 'start' && this.end && +this.end >= +selected.date) {
          this.selectedDate.emit({start: selected.date, end: this.end, active: undefined})
        } else if (this.side == 'end' && this.start && +this.start <= +selected.date) {
          this.selectedDate.emit({start: this.start, end: selected.date, active: undefined})
        } else {
          this.active = selected.date
          this.selectedDate.emit({start: selected.date, end: selected.date, active: selected.date})
        }
      } else if (isBefore(selected.date, this.active)) {
        this.active = undefined
        this.selectedDate.emit({start: selected.date, end: this.end, active: undefined})
      } else {
        this.active = undefined
        this.selectedDate.emit({start: this.start, end: selected.date, active: undefined})
      }
      this.changeRef.markForCheck()
    }
  }

  hoverDate(date: { date: Date, available: boolean }) {
    if (!this.readonly && date.available) {
      if (this.active) {
        if (isBefore(date.date, this.active)) {
          this.hoveredDate.emit({start: date.date, end: this.active})
        } else {
          this.hoveredDate.emit({start: this.active, end: date.date})
        }
      }
      this.changeRef.markForCheck()
    }
  }

  nextMonth() {
    this.date = addMonths(this.date, 1)
    this.updateDays()
  }

  prevMonth() {
    this.date = addMonths(this.date, -1)
    this.updateDays()
  }

  private isDayInRange(currentDay: Date): boolean {
    if (!this.start || !this.end) return false;
    const startDay = startOfDay(this.start);
    const endDay = startOfDay(this.end);
    if (isAfter(startDay, endDay)) return false;
    return isWithinInterval(currentDay, { start: startDay, end: endDay });
  }

  private beginningOfWeek(date: Date, firstDayOfWeek: number): Date {
    const dayOfWeek = date.getDay()
    const diff = (dayOfWeek + 7 - firstDayOfWeek) % 7
    return startOfDay(addDays(date, -diff))
  }

  private updateDays() {
    this.month = format(this.date, 'MMM yyyy')
    const firstDayOfWeek = this.firstDayOfWeek ? Number(this.firstDayOfWeek) : this.datesService.accountFirstDayOfWeek
    let currentDay = this.beginningOfWeek(new Date(this.date.getTime()), firstDayOfWeek)
    if (currentDay.getDate() > 1 && currentDay.getMonth() === this.date.getMonth()) {
      currentDay = addDays(currentDay, -7)
    }
    this.days = []
    this.weekdayNames = []
    for (let week = 0; week < 6; week += 1) {
      const weekdays = []
      for (let day = firstDayOfWeek; day < 7 + firstDayOfWeek; day += 1) {
        const index = day - firstDayOfWeek
        this.weekdayNames[index] = format(currentDay, 'EEEE').slice(0, 2)
        weekdays[index] = {
          number: format(currentDay, 'd'),
          date: new Date(currentDay.getTime()),
          current: currentDay.getMonth() === this.date.getMonth(),
          today: isToday(currentDay),
          available: (!this.min || isAfter(currentDay, this.min)) && (!this.max || isBefore(currentDay, this.max)),
          start: startOfDay(currentDay).getTime() === (this.start && startOfDay(this.start).getTime()),
          end: startOfDay(currentDay).getTime() === (this.end && startOfDay(this.end).getTime()),
          range: this.isDayInRange(currentDay),
          expand: (this.side == 'start') ? isBefore(currentDay, this.end) : isAfter(currentDay, this.start),
        }
        currentDay = addDays(currentDay, 1)
      }
      this.days[week] = weekdays
    }
    this.changeRef.markForCheck()
  }
}